import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { ExpenseEntry } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Textarea,
} from '@aeris/ui'

import { voidExpense } from '../lib/api/expenses.ts'
import { ApiError } from '../lib/api.ts'

/**
 * Desfazer um lançamento (F146). Nada se apaga: a linha fica, riscada, com o
 * motivo e quem desfez.
 */
export const ExpenseVoidDialog = ({
  entry,
  onDone,
  onClose,
}: {
  entry: ExpenseEntry
  /** `cashReturned`: o dinheiro voltou para a gaveta de onde saiu. */
  onDone: (result: { cashReturned: boolean }) => void
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const undo = useMutation({
    mutationFn: async () =>
      await voidExpense(entry.id, {
        version: entry.version,
        reason: reason.trim(),
      }),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      onDone({ cashReturned: response.cashReturned })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível desfazer.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Desfazer lançamento"
        description={`${entry.description} · ${formatMoney(parseAmount(entry.amount))}`}
        className="max-w-md"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (reason.trim().length < 3) return
            setError(null)
            undo.mutate()
          }}
        >
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            {entry.paymentSource === 'cash'
              ? 'Saiu do caixa: se aquele caixa ainda está aberto, o dinheiro volta para a gaveta.'
              : 'A linha fica na lista, riscada, e deixa de somar.'}
          </p>
          <Field label="Por que está desfazendo" required>
            {(field) => (
              <Textarea
                {...field}
                value={reason}
                maxLength={500}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
              />
            )}
          </Field>

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={reason.trim().length < 3}
              loading={undo.isPending}
            >
              Desfazer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
